/**
 * KPITable 逻辑层 → ProTableListForm subArray 指标列的翻译器（方案二第二消费者）。
 *
 * 调用 KPITable/kpiColumnRules 的 computeShowColKeys/computeColConfig/computeKpiCodePatch，
 * 把「列可见性 + 单列可编辑/必填」规则翻译成 SubArraySpec 的 columns（SubColumnMeta[]），
 * 供 CooperationTableList 的 kpi_values 子数组渲染 —— 一份规则、两处渲染。
 *
 * 【KPI 列对齐原版 KPITable（拆单场景）】：
 *  - kpi_code        下拉（原版 kpiCodeOptions：按 platform 过滤 + 已选禁用）+ 只读名称/验收标准 tag
 *  - directKpi       只读展示「是否 + 正/负向指标 Tag」（原版 directKpi render；仅 numeric 显示 Tag）
 *  - expect_value    按 kpi_schema.type 分派受控组件（numeric→InputNumber / percent→SInputPercent / text→Input）
 *  - show_for_out    Checkbox（值=布尔，原版 singleCheckbox children="可见"）
 *  - accept_flag     Checkbox（选中→true；取消→清空 accept_remark，原版 onChange 联动）
 *  - accept_remark   单行 Input（对齐面板 componentClass:'input'；disabled: !accept_flag；maxLength 200）
 *  - actual_value    按 kpi_schema.type 分派受控组件 + 只读时 percent×100% 展示
 *  - result          达预期/未达预期（Select 或 只读 Tag）
 *
 * ⚠️ 引擎内置 select/input/checkbox 子列不支持 disabled / 跨字段联动 / 按 schema 分派，
 *    故本表全部列用 type:'custom' + render 自行掌控股件；patch 支持多字段（联动清空）。
 * ⚠️ 高级能力（CPM 自动拉取 / 三方数据弹窗 / actual_value_extra）暂未承载，Phase E 细化。
 */
import React from 'react';
import { Select, InputNumber, Checkbox, Space, Tag, Tooltip } from '@bedrock/components';
import type { TagColor } from '@bedrock/components/lib/Tag';
import { ComposingSafeInput, CELL_SELECT_DROPDOWN_PROPS, type SubColumnMeta, type SubArraySpec } from '@mpc/common/src/components/ProTableListForm';
import { computeShowColKeys, computeColConfig, computeKpiCodePatch } from '../KPITable/kpiColumnRules';
import { KPIValueShowComponent } from '../KPITable/index';
import { ApplyToWholeOrderAction } from './ApplyToWholeOrderAction';
import type { CoverMode } from './applyToWholeOrder';
import SInputPercent from '#components/SForm/SFormElement/InputPercent';
import { enums } from '#services/api';

/** buildKpiSubArraySpec 入参：来自 CooperationTableList 的契约（demo 用 mock） */
export interface KpiSubArraySpecParams {
  /** 全量指标列表（useKPIList 结果），kpi_code 下拉选项来源 */
  kpiList: any[];
  platforms: string[];
  view: boolean;
  moreKpi?: boolean;
  detailState?: string;
  structuredVersion?: number;
  groupType?: string;
  colKeys?: any[];
  needResultStates: string[];
  isInner: boolean;
  $sType: string;
  productArea?: string;
  /** 是否实际执行：false 时整列只读（原版 need_actual_execution 注入） */
  need_actual_execution?: boolean;
  /** 拆单「已有执行周期」纬度：锁定 kpi_code 列为只读 */
  disableKpiCodeEdit?: boolean;
  /** 拆单「已有梯度」纬度：锁定 expect_value 列为只读 */
  disableExpectValueEdit?: boolean;
  /** 拆单「已有执行周期」纬度：隐藏「+添加指标」（对齐面板 KPITable 的 disableAdd） */
  disableAdd?: boolean;
  /** 拆单「已有执行周期」纬度：禁用指标行删除（对齐面板 KPITable 的 disableDelete） */
  disableDelete?: boolean;
  /**
   * 【整体必填】考核指标是否必须有子项。来源对齐面板模式 NewCooperate：
   * `!view && ['cooperateKpiRequired'].includes(cooperateType)`（kpiTableProps.required）。
   * 驱动组标题星号 + 引擎空子项校验（滚动到「+添加指标」按钮）+ 至少保留 1 条（minItems）。
   */
  required?: boolean;
  /**
   * 【行级整组隐藏】该行 KPI 是否参与渲染。由 CooperationTableList 注入，判定对齐面板
   * `CooperationFormList/index.tsx:615`：`need_cooperate_account_supplier_settle_price
   * && 该行 need_actual_execution === false` → 该条合作内容的 KPI 整块消失。
   *
   * 返回 false 时引擎让该行该组全部子列渲染为空、不计行高、并跳过校验（见 SubArraySpec.visibleRow）。
   *
   * ⚠️ 当前由 `CooperationTableList` 的 `ENABLE_KPI_ROW_HIDE` 总开关**关闭**（传 undefined），
   * 即全部行可见。原因见该开关注释：该规则服务非拆单场景，拆单实测中会让整列消失。
   */
  visibleRow?: (row: any) => boolean;
  checkCPM?: (code?: string) => boolean;
  /**
   * 【应用至整单】提供时在「考核指标」组渲染子列动作按钮（见 `SubArraySpec.renderRowAction`）。
   * 入参：该按钮所在**行的行数据** + 用户在弹层里选的 `coverMode`
   * （`retain` 保留原值 / `overwrite` 覆盖原值），由 `CooperationTableList` 承接写回。
   * 不提供则不渲染按钮（如策划拆单预览态）。
   */
  onApplyToTheWholeOrder?: (row: any, coverMode: CoverMode) => void;
}

/** 验收标准备注最大长度（对齐面板 KPITable accept_remark 的 maxLength） */
const ACCEPT_REMARK_MAX_LENGTH = 200;
/** 数值列允许的最大小数位（对齐面板 KPITable 的 kpiSchemaToElement） */
const NUMERIC_DECIMAL_LIMIT = 4;

/**
 * 数值列的格式校验（对齐面板 KPITable 的 kpiSchemaToElement rules）：
 * 数值型指标禁止负数、小数位不超过 4 位。非数值型（text）不做校验。
 *
 * ⚠️ 小数位超限的提示文案逐字对齐面板：percent 指标面板也写「百分比只能输入两位小数」
 * （实际规则同为 4 位上限），此处照抄以保持两形态提示一致，矛盾点记于回归清单待确认。
 */
function validateNumericValue(value: any, it: any): string | undefined {
  if (it?.kpi_schema?.type !== 'numeric') return undefined;
  if (value === undefined || value === null || value === '') return undefined;
  if (Number(value) < 0) return '不能输入负数';
  const decimals = String(value).split('.')[1]?.length ?? 0;
  if (decimals > NUMERIC_DECIMAL_LIMIT) {
    return it.kpi_schema.percent === 1 ? '百分比只能输入两位小数' : '只能输入4位小数';
  }
  return undefined;
}

/**
 * kpi_code 下拉 options（对齐面板 KPITable 的 kpiCodeOptions :461-479）：
 *  - 指标字典（KPIList）为空时，用**本行已选子项的 kpi_schema** 兜底（面板 :462-465）
 *  - 按 platform 过滤
 *  - **本行**已选的指标不可重复选择（面板 :475 的 `data.some(...)`，data 即该行的 kpi_values）
 */
function buildKpiCodeOptions(params: KpiSubArraySpecParams, items: any[]) {
  const dict = params.kpiList?.length ? params.kpiList : [];
  const source = dict.length ? dict : (items || []).map((it) => it?.kpi_schema).filter(Boolean);
  return (source || [])
    .filter((item: any) => params.platforms?.includes?.(item?.platform))
    .map((item: any) => ({
      name: item?.name,
      value: item?.code,
      // 已经选择的考核指标，本行内不可再次选择
      disabled: !!(items || []).some((n: any) => n?.kpi_code === item?.code),
    }));
}

/** 只读单元格：单行文本 + 可选 Tag（对齐原版 ViewCellContent 的 one-line-ellipsis + tag） */
function ReadonlyText({ text, tag }: { text: string; tag?: { text: string; color: TagColor } }) {
  return (
    <span className="flex items-center gap-1 min-w-0">
      <span className="truncate">{text}</span>
      {tag && <Tag size="small" color={tag.color} text={tag.text} />}
    </span>
  );
}

/** 将 showColKeys 选中的列 key 翻译成 SubColumnMeta（title/width/type:'custom' + render） */
function buildColumns(params: KpiSubArraySpecParams): SubColumnMeta<any>[] {
  const colConfigInput = {
    view: params.view,
    need_actual_execution: params.need_actual_execution,
    moreKpi: params.moreKpi,
    colKeys: params.colKeys,
    $sType: params.$sType,
    product_area: params.productArea,
    checkCPM: params.checkCPM || (() => false),
  };
  const showKeys = computeShowColKeys({
    view: params.view,
    moreKpi: params.moreKpi,
    detailState: params.detailState,
    platforms: params.platforms,
    structuredVersion: params.structuredVersion,
    groupType: params.groupType,
    colKeys: params.colKeys,
    isInner: params.isInner,
    needResultStates: params.needResultStates,
  });
  // 用户要求：直接考核置于「考核指标之后、指标预期之前」→ 重排列顺序（原 computeShowColKeys 把 directKpi push 在 expect_value 之后，此处分派层按需重排，不影响 KPITable 本体）
  const COL_ORDER = ['kpi_code', 'directKpi', 'expect_value', 'show_for_out', 'accept_flag', 'accept_remark', 'actual_value', 'result'];
  const orderedKeys = showKeys.filter((k: string) => k !== 'do').sort((a: string, b: string) =>
    (COL_ORDER.indexOf(a) < 0 ? 999 : COL_ORDER.indexOf(a)) - (COL_ORDER.indexOf(b) < 0 ? 999 : COL_ORDER.indexOf(b)));

  const defs: Record<string, { title: string; width: number }> = {
    kpi_code: { title: '考核指标', width: 160 },
    directKpi: { title: '直接考核', width: 120 },
    expect_value: { title: params.platforms.includes('MPC') ? '指标预期' : '达标指数', width: 280 },
    show_for_out: { title: '供应商可见', width: 104 },
    accept_flag: { title: '是否验收标准', width: 148 },
    accept_remark: { title: '验收标准备注', width: 280 },
    actual_value: { title: '指标结果', width: 250 },
    result: { title: '是否达预期', width: 150 },
  };
  // 必填子列：仅「考核指标」「指标预期」两列。引擎据 SubColumnMeta.required 渲染表头星号
  // （ProTableListForm subTitle）并执行必填校验（findFirstDoc），无需额外 rules。
  const REQUIRED_COL_KEYS = ['kpi_code', 'expect_value'];
  // 只读列不加必填（对齐原版 KPITable :522/:618：disableKpiCodeEdit/disableExpectValueEdit
  // 命中时整列只读 → required 无意义，避免历史数据被实时标红）
  const isColRequired = (key: string) =>
    !params.view &&
    REQUIRED_COL_KEYS.includes(key) &&
    !(key === 'kpi_code' && params.disableKpiCodeEdit) &&
    !(key === 'expect_value' && params.disableExpectValueEdit);
  // 需要数值格式校验的列：面板的 kpiSchemaToElement 只作用于「指标预期」「指标结果」两列
  const NUMERIC_VALIDATE_KEYS = ['expect_value', 'actual_value'];
  // 参与校验的列（必填 ∪ 格式）= 需要向引擎声明「是否受控」的列（expect_value 两处重复，无副作用）
  const VALIDATED_COL_KEYS = [...REQUIRED_COL_KEYS, ...NUMERIC_VALIDATE_KEYS];
  /**
   * 该子项此字段当前是否渲染为**受控（可编辑）控件** —— 与下方各 key 的 render 分支只读判据**逐一对应**。
   *
   * 引擎只校验受控字段（`SubColumnMeta.editable`）：只读展示 / disabled 控件用户改不了，
   * 对它报「必填」会造成**提交被拦却无从修复**的死锁（如 `conf.view` 命中的子项、未选指标的
   * 空行 —— 后者连输入控件都没有）。
   *
   * ⚠️ 必须与 render 分支**同源**：改动任一只读条件时两边都要改，否则会出现
   * 「渲染成只读、校验仍必填」（或反之）的漂移。
   */
  const isSubColEditable = (key: string, it: any): boolean => {
    // ① 无 kpi_schema：expect_value / actual_value 渲染为纯展示文本（见下方对应 render 分支），无输入控件。
    //    kpi_code 无此分支（无 schema 时仍是可选的下拉），故不参与本判据。
    if (!it?.kpi_schema && (key === 'expect_value' || key === 'actual_value')) return false;
    // ② conf.view 为**行级**只读（按子项计算），三列 render 均以其为纯展示分支
    if (computeColConfig(key, it, colConfigInput).view) return false;
    // ③ 列级禁用（拆单「已有梯度」纬度）：控件渲染为 disabled
    if (key === 'kpi_code') return !params.disableKpiCodeEdit;
    if (key === 'expect_value') return !params.disableExpectValueEdit;
    return true;
  };

  // 按 kpi_schema.type 分派数值输入控件：numeric(percent)→SInputPercent，numeric→InputNumber，text→Input
  const numericInput = (
    key: string,
    it: any,
    patch: (p: Record<string, unknown>) => void,
    percent: boolean,
    disabled?: boolean,
  ) => {
    if (percent) {
      return (
        <SInputPercent
          size="small"
          value={it[key]}
          valueType="number"
          digits={NUMERIC_DECIMAL_LIMIT}
          disabled={disabled}
          style={{ width: '100%' }}
          onChange={(v: any) => patch({ [key]: v })}
        />
      );
    }
    return (
      <InputNumber
        size="small"
        direction="vertical"
        value={it[key]}
        toFixed={NUMERIC_DECIMAL_LIMIT}
        disabled={disabled}
        style={{ width: '100%' }}
        onChange={(v: any) => patch({ [key]: v })}
      />
    );
  };

  // 按 kpi_schema.type 生成可编辑控件（expect_value/actual_value 共用，对齐原版 kpiSchemaToElement）
  const valueEditControl = (key: string, it: any, patch: (p: Record<string, unknown>) => void, disabled?: boolean) => {
    const type = it.kpi_schema?.type;
    const percent = it.kpi_schema?.percent === 1;
    if (type === 'numeric') return numericInput(key, it, patch, percent, disabled);
    return (
      // 组合输入安全：避免上游重渲染强写 DOM 打断拼音输入
      <ComposingSafeInput
        size="small"
        value={it[key] ?? ''}
        maxLength={500}
        disabled={disabled}
        placeholder="请输入"
        style={{ width: '100%' }}
        onChange={(e) => patch({ [key]: e.target.value })}
      />
    );
  };

  const columns: SubColumnMeta<any>[] = [];
  for (const key of orderedKeys) {
    // 删除按钮由 ProTableListForm 的 subArray 操作列（`${sub.key}Op`）承担，不在此重复加 do 列。
    if (key === 'do') continue;
    const def = defs[key];
    if (!def) continue;

    const col: SubColumnMeta<any> = {
      key,
      field: key,
      title: def.title,
      width: def.width,
      type: 'custom',
      // 必填：驱动表头星号 + 引擎必填校验/实时标红（校验消息取引擎统一的「此项必填」）。
      // 只读展示（view/只读纬度命中）不加必填——既无需星号，也避免历史数据缺值时被实时标红。
      required: isColRequired(key),
      // 数值格式校验（非负 + ≤4 位小数，对齐面板 KPITable 的 kpiSchemaToElement）。
      // view 态为纯展示，不参与校验（对齐面板 SFormItem 的 view 模式）。
      validator: !params.view && NUMERIC_VALIDATE_KEYS.includes(key) ? validateNumericValue : undefined,
      // 只校验受控字段：只读展示 / disabled 控件（含无 kpi_schema 的空行）退出校验。
      // 仅对参与校验的列声明（其余列本无校验规则，声明无意义）。
      editable: VALIDATED_COL_KEYS.includes(key) ? (it: any) => isSubColEditable(key, it) : undefined,
    };

    if (key === 'kpi_code') {
      col.render = (it: any, patch: (p: Record<string, unknown>) => void, items: any[]) => {
        const conf = computeColConfig('kpi_code', it, colConfigInput);
        const readonly = conf.view;
        const kpiSchema = it.kpi_schema || params.kpiList.find((x: any) => x.code === it.kpi_code);
        const showTag = params.structuredVersion >= 1 && it.accept_flag;
        if (readonly) {
          return (
            <ReadonlyText
              text={kpiSchema?.name || '--'}
              tag={showTag ? { text: '验收标准', color: 'orange' } : undefined}
            />
          );
        }
        return (
          <Select
            size="small"
            value={it.kpi_code || undefined}
            placeholder="请选择"
            options={buildKpiCodeOptions(params, items)}
            disabled={params.disableKpiCodeEdit}
            style={{ width: '100%' }}
            {...CELL_SELECT_DROPDOWN_PROPS}
            onChange={(v: any) => patch(computeKpiCodePatch(v, params.kpiList, params.moreKpi))}
          />
        );
      };
    } else if (key === 'directKpi') {
      col.render = (it: any) => {
        const positive = enums.DemandKpiPositiveEnum.pick(it?.kpi_schema?.positive);
        const isNumeric = it?.kpi_schema?.type === 'numeric';
        const conf = positive?.value === 0
          ? { color: 'green', tip: '>=指标预期为达预期' }
          : { color: 'red', tip: '<=指标预期为达预期' };
        return (
          <Space size={[8, 0]}>
            {isNumeric ? '是' : '否'}
            {isNumeric && (
              <Tooltip title={conf.tip}>
                <Tag size="small" text={positive?.name} color={(`light-${conf.color}`) as TagColor} />
              </Tooltip>
            )}
          </Space>
        );
      };
    } else if (key === 'expect_value') {
      col.render = (it: any, patch: (p: Record<string, unknown>) => void) => {
        if (!it.kpi_schema) return it.expect_value ? <KPIValueShowComponent value={it.expect_value} record={it} /> : '--';
        const conf = computeColConfig('expect_value', it, colConfigInput);
        // 只读展示：仅「无编辑权限（view）」→ 纯文本
        if (conf.view) return <KPIValueShowComponent value={it.expect_value} record={it} />;
        // disableExpectValueEdit（拆单「已有梯度」纬度）：值由外部注入，展示 disabled 输入控件
        // 而非纯文本，与「考核指标」列保持一致（对齐面板 KPITable :629-638）
        return valueEditControl('expect_value', it, patch, !!params.disableExpectValueEdit);
      };
    } else if (key === 'show_for_out') {
      col.render = (it: any, patch: (p: Record<string, unknown>) => void) => {
        const conf = computeColConfig('show_for_out', it, colConfigInput);
        if (conf.view) return it.show_for_out ? '可见' : '--';
        return (
          <Checkbox
            checked={!!it.show_for_out}
            onChange={(e) => patch({ show_for_out: e.target.checked })}
          >
            可见
          </Checkbox>
        );
      };
    } else if (key === 'accept_flag') {
      col.render = (it: any, patch: (p: Record<string, unknown>) => void) => {
        const conf = computeColConfig('accept_flag', it, colConfigInput);
        if (conf.view) return it.accept_flag ? '验收标准' : '--';
        return (
          <Checkbox
            checked={!!it.accept_flag}
            onChange={(e) => {
              if (e.target.checked) {
                patch({ accept_flag: true });
              } else {
                // 取消「验收标准」→ 清空验收标准备注（对齐原版 accept_flag onChange :822-834）
                patch({ accept_flag: false, accept_remark: '' });
              }
            }}
          >
            验收标准
          </Checkbox>
        );
      };
    } else if (key === 'accept_remark') {
      col.render = (it: any, patch: (p: Record<string, unknown>) => void) => {
        const conf = computeColConfig('accept_remark', it, colConfigInput);
        // 未勾选「验收标准」时备注不可填（对齐面板 accept_flag 的联动 disabled）
        const disabled = !it.accept_flag;
        if (conf.view) return it.accept_remark ? <KPIValueShowComponent value={it.accept_remark} record={it} /> : '--';
        // 单行输入（对齐面板 componentClass:'input'，maxLength 200）
        // 组合输入安全：避免上游重渲染强写 DOM 打断拼音输入
        return (
          <ComposingSafeInput
            size="small"
            value={it.accept_remark ?? ''}
            maxLength={ACCEPT_REMARK_MAX_LENGTH}
            disabled={disabled}
            placeholder="请填写验收标准备注"
            style={{ width: '100%' }}
            onChange={(e) => patch({ accept_remark: e.target.value })}
          />
        );
      };
    } else if (key === 'actual_value') {
      col.render = (it: any, patch: (p: Record<string, unknown>) => void) => {
        if (!it.kpi_schema) return '--';
        const conf = computeColConfig('actual_value', it, colConfigInput);
        if (conf.view) {
          return (
            <Space size={[8, 8]} wrap>
              <KPIValueShowComponent value={it.actual_value} record={it} />
              {it.label_obj?.label_name && (
                <Tag color="light-red" text={it.label_obj.label_name} size="small" />
              )}
            </Space>
          );
        }
        return valueEditControl('actual_value', it, patch);
      };
    } else if (key === 'result') {
      col.render = (it: any, patch: (p: Record<string, unknown>) => void) => {
        const conf = computeColConfig('result', it, colConfigInput);
        if (conf.view) {
          if (typeof it.result === 'undefined') return '--';
          return <ReadonlyText text={it.result ? '达预期' : '未达预期'} />;
        }
        return (
          <Select
            size="small"
            value={it.result === true ? 1 : it.result === false ? 0 : undefined}
            placeholder="请选择"
            options={[
              { name: '达预期', value: 1 },
              { name: '未达预期', value: 0 },
            ]}
            style={{ width: '100%' }}
            {...CELL_SELECT_DROPDOWN_PROPS}
            /* 2 选项下拉不加搜索框（覆盖须写在展开之后，否则被收口常量置回 true） */
            showSearch={false}
            onChange={(v: any) => patch({ result: v === 1 })}
          />
        );
      };
    }

    columns.push(col);
  }
  return columns;
}

/**
 * 「考核指标」组标题。
 *
 * 单一来源：既作子数组组标题，也作「应用至整单」二次确认文案里的区块名 ——
 * 两处必须同名，各写一份字面量会在改标题时漏掉确认文案。
 */
const KPI_SUB_ARRAY_TITLE = '考核指标';

/** 构建 kpi_values 的 SubArraySpec（参数化，CooperationTableList useMemo 每次用当前行参数调用） */
export function buildKpiSubArraySpec(params: KpiSubArraySpecParams): SubArraySpec<any, any> {
  const { onApplyToTheWholeOrder } = params;
  return {
    key: 'kpi_values',
    title: KPI_SUB_ARRAY_TITLE,
    emptyLabel: '指标',
    // 整体必填（对齐面板模式 kpiTableProps.required → cooperateKpiRequired 判定）：
    // 组标题星号 + 引擎空子项校验（有错时滚动到「+添加指标」按钮）
    required: !!params.required,
    // 拆单「已有执行周期」纬度：隐藏「+添加指标」/ 禁用指标行删除
    // （对齐面板 KPITable 的 disableAdd/disableDelete）
    disableAdd: params.disableAdd,
    disableDelete: params.disableDelete,
    // 整体必填时至少保留 1 条指标（对齐面板 KPITable 的 ableDelete = !required || data.length > 1）
    minItems: params.required ? 1 : 0,
    // 行级整组隐藏（该行无需实际执行 → KPI 整块消失，对齐面板 index.tsx:615 的 hide）
    visibleRow: params.visibleRow,
    // ⚠️ 真实数据子项可能缺 id 且缺 index_id（空行/新增），必须基于行 id + 下标兜底唯一 key，
    //    否则多条都回退成 `undefined-kpi-undefined` 触发 React 同 key 冲突（运行时实测暴露）。
    getItems: (row) => (row.kpi_values || []).map((it: any, index: number) => ({ ...it, id: it.id ?? `${row.id}-kpi-${it.index_id ?? index}` })),
    setItems: (row, items) => ({ ...row, kpi_values: items }),
    makeDefault: () => ({ id: `kpi-${Date.now()}`, kpi_code: '', expect_value: '', accept_flag: false, custom: 0 }),
    columns: buildColumns(params),
    idPrefix: 'kpi',
    // 「应用至整单」子列动作（对齐卡片模式 KPITable 的同名按钮）：以本行 kpi_values 为源写入整单。
    // 弹层里选「保留原值 / 覆盖原值」，确认后带回 coverMode（见 ApplyToWholeOrderAction）。
    // 显隐与「+添加指标」同门控。
    renderRowAction: onApplyToTheWholeOrder
      ? (row) => (
          <ApplyToWholeOrderAction
            targetName={KPI_SUB_ARRAY_TITLE}
            onApply={(coverMode) => onApplyToTheWholeOrder(row, coverMode)}
          />
        )
      : undefined,
    // 落点 = **第二子列**（拆单场景即「直接考核」列），与「+添加指标」各占末尾一行、视觉并排。
    // ⚠️ 显式写出而非依赖引擎默认值：权益两区块刻意用 `'first'`（与添加同 cell），
    // 两处落点不同是**有意的**，在此显式标注以免后续被「统一」掉（见 dev.spec 陷阱 20）。
    rowActionColumn: 'second',
  };
}
